import { Link } from '@tanstack/react-router';

import { CtaPill } from './primitives/CtaPill';
import { Wordmark } from './primitives/Wordmark';

const navLinks = [
    { href: '#team', label: 'The team' },
    { href: '#how', label: 'How we work' },
    { href: '#what', label: 'What we build' },
    { label: 'Security', href: '#security' },
];

/**
 * Sticky marketing header.
 *
 * The mockup hides the nav below its `md` breakpoint. Two things replace that.
 *
 * The nav is a horizontal scroll rail, so five links never wrap and never
 * disappear — unlike `md:hidden`, the navigation stays reachable on the
 * devices most likely to need it. `min-w-0` on the rail is load-bearing:
 * without it `flex-1` will not let the nav shrink below its content width.
 *
 * The header itself wraps, and so does the right-hand group. Measured at
 * 390px the wordmark, "Sign in" and the CTA pill need about 480px on one row,
 * so without wrapping the primary call to action is clipped off the right
 * edge entirely. Wrapping costs a second ~46px row on a phone (a third at
 * 320px) and nothing at all on a laptop, which is a better trade than an
 * invisible CTA.
 *
 * This is only sticky because `pages/landing/index.tsx` uses `overflow-x-clip`
 * rather than `overflow-x-hidden` — see the note there.
 */
export function Header() {
    return (
        <header className="sticky top-0 z-50 flex w-full flex-wrap items-center gap-x-6 gap-y-3 border-b border-border bg-card px-[clamp(20px,4vw,48px)] py-3">
            <a href="#top" className="shrink-0">
                <Wordmark />
                <span className="sr-only">MohairNotes — back to top</span>
            </a>

            <nav
                aria-label="Primary"
                className="flex min-w-0 flex-1 [scrollbar-width:none] items-center gap-[clamp(14px,2vw,30px)] overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
                {navLinks.map((link) => (
                    <a
                        key={link.href}
                        href={link.href}
                        className="font-mono text-[12px] tracking-[0.08em] whitespace-nowrap text-muted-foreground uppercase transition-colors duration-200 hover:text-primary"
                    >
                        {link.label}
                    </a>
                ))}
            </nav>

            <div className="flex min-w-0 flex-wrap items-center justify-end gap-4">
                <Link
                    to="/login"
                    className="font-mono text-[12px] tracking-[0.08em] whitespace-nowrap text-muted-foreground uppercase transition-colors duration-200 hover:text-primary"
                >
                    Sign in
                </Link>
                <CtaPill href="/login" className="px-6 py-3">
                    Book a working session
                </CtaPill>
            </div>
        </header>
    );
}
